import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ShieldCheck, Users, FileText, Lightning, ChartBar, GitBranch,
  Funnel, Upload, Link as LinkIcon, Bell, Envelope, Gear,
  PaintBrush, Robot, Lock, Eye, Table, ArrowRight, CheckCircle,
  Clipboard, Timer, UserCircle, Buildings, Globe, Heart,
  ShieldStar, Database, WifiHigh, TreeStructure
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ───────── helpers ───────── */
function hexToHSL(hex) {
  if (!hex) return null;
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  } else { s = 0; }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/* ───────── typewriter ───────── */
const TYPEWRITER_LINES = [
  "Stop drowning in spreadsheets.",
  "Auto-assign leads while you sip chai.",
  "Track every customer touchpoint — magically.",
  "Your branches, finally on the same page.",
  "Documents that don't play hide and seek.",
  "Reports your boss will actually read.",
  "Built for humans. Not spreadsheet wizards.",
];

function useTypewriter(lines, typeSpeed = 55, eraseSpeed = 30, pauseMs = 2200) {
  const [text, setText] = useState('');
  const [lineIdx, setLineIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const line = lines[lineIdx];
    let timeout;
    if (!deleting && charIdx <= line.length) {
      timeout = setTimeout(() => {
        setText(line.slice(0, charIdx));
        setCharIdx(c => c + 1);
      }, charIdx === line.length ? pauseMs : typeSpeed);
    } else if (!deleting && charIdx > line.length) {
      setDeleting(true);
    } else if (deleting && charIdx >= 0) {
      timeout = setTimeout(() => {
        setText(line.slice(0, charIdx));
        setCharIdx(c => c - 1);
      }, eraseSpeed);
    } else {
      setDeleting(false);
      setCharIdx(0);
      setLineIdx(i => (i + 1) % lines.length);
    }
    return () => clearTimeout(timeout);
  }, [charIdx, deleting, lineIdx, lines, typeSpeed, eraseSpeed, pauseMs]);

  return text;
}

/* ───────── features ───────── */
const FEATURES = [
  { icon: GitBranch, title: 'Multi-Branch Management', desc: 'Create and manage unlimited branches. Each gets its own workspace, records and user hierarchy.', joke: "Finally, a family tree that doesn't cause arguments at Thanksgiving." },
  { icon: Users, title: 'Role-Based Access', desc: 'Super Admin, Branch Admin, Employee — everyone sees only what they should. Field-level permissions on every schema.', joke: "Because not everyone needs to see Karen's salary." },
  { icon: Table, title: 'Dynamic Schemas via CSV', desc: 'Upload any CSV and the system auto-detects columns, types, and creates a fully queryable data structure.', joke: "CSV: Comma Separated Victory." },
  { icon: Funnel, title: 'Advanced Filtering', desc: 'Build complex AND/OR filters across any field. Sort by multiple columns. Export matches to CSV instantly.', joke: "Find that one needle in 10,000 haystacks. We timed it — 0.3 seconds." },
  { icon: Robot, title: 'Auto-Allocation Rules', desc: 'Set conditions and priorities. Incoming records get auto-assigned to the right person, no human bottleneck.', joke: "Robots taking jobs? Nah, just the boring parts." },
  { icon: FileText, title: 'Document Management', desc: 'Upload, preview PDFs and images inline, share documents across branches with granular access controls.', joke: "No more 'Please find attached the attachment that was not attached.'" },
  { icon: LinkIcon, title: 'Temporary Access Links', desc: 'Share record access across branches with time-limited, policy-authenticated links. Full approval workflow.', joke: "Like a VIP pass, but for spreadsheets." },
  { icon: ChartBar, title: 'Dashboard Analytics', desc: 'Real-time status distribution, branch performance charts, activity trends, and leaderboards.', joke: "Graphs go up, boss is happy. That's the whole strategy." },
  { icon: Bell, title: 'Reminders & Interactions', desc: 'Never miss a follow-up. Log every call, email, and meeting. Set reminders that actually remind.', joke: "Unlike your New Year's resolution tracker." },
  { icon: Envelope, title: 'SMTP & Webhooks', desc: 'Configure email delivery and webhook integrations. Get notified where you want, how you want.', joke: "Notifications done right. No carrier pigeons involved." },
  { icon: Eye, title: 'In-Browser Document Preview', desc: 'View PDFs, images, and documents right inside the app. No downloads, no desktop clutter.', joke: "Your Downloads folder sends its thanks." },
  { icon: PaintBrush, title: 'Custom Themes', desc: '10 built-in theme swatches. Create your own. Apply brand colors across the entire platform in one click.', joke: "Corporate asked for 'innovation blue'. We have 47 shades of it." },
  { icon: Lightning, title: 'Drag & Drop Import', desc: 'Drop a CSV right onto the records table. Instant import. Admin-only to prevent accidental chaos.', joke: "Drag. Drop. Done. No magic wand required." },
  { icon: Clipboard, title: 'Audit Logging', desc: 'Every action tracked — who did what, when, where. Toggle logging per resource. Full export support.', joke: "Big Brother, but the helpful kind." },
  { icon: ShieldCheck, title: 'Editable Field Controls', desc: 'Mark fields as required, visible, or editable. Super admins override everything. Schema-level governance.', joke: "With great power comes great editability." },
  { icon: UserCircle, title: 'User Leaderboards', desc: 'Gamify operations. Track who closes the most, responds fastest, and delivers results.', joke: "Finally, proof that you're the office MVP." },
];

/* ───────── pain points ───────── */
const PAIN_POINTS = [
  {
    domain: 'Insurance',
    icon: ShieldStar,
    problem: 'Policy data lives in 15 different Excel files across 8 branches. Renewals get missed, agents duplicate work, and nobody knows who owns which customer. Branch managers spend Mondays reconciling data instead of selling.',
    solution: 'Upload your master policy list once. The system maps customers to branches automatically, tracks every interaction, sends renewal reminders, and shows real-time performance dashboards per branch. Auto-allocation ensures no lead falls through the cracks.'
  },
  {
    domain: 'Banking & Finance',
    icon: Buildings,
    problem: 'Loan applications shuffle between branches via email attachments. Document versions get mixed up. Compliance audits are nightmares because there\'s no single trail of who approved what and when.',
    solution: 'Centralized document management with in-browser preview, full audit trails for every action, temporary access links for cross-branch reviews, and role-based access ensuring only authorized personnel see sensitive financial data.'
  },
  {
    domain: 'Healthcare',
    icon: Heart,
    problem: 'Patient referrals between departments get lost. Follow-up schedules depend on sticky notes. Regulatory compliance requires tracking every data access, but your current system has zero visibility.',
    solution: 'Dynamic schemas handle different data structures per department. Reminder system ensures no follow-up is missed. Comprehensive audit logging tracks every access for HIPAA-style compliance. Branch-level isolation protects patient data.'
  },
  {
    domain: 'Real Estate',
    icon: Buildings,
    problem: 'Agents hoard leads in personal spreadsheets. Property listings are outdated across offices. Management has no idea which agent is actually performing and which is just "busy".',
    solution: 'Auto-allocation distributes leads fairly based on rules you set. Leaderboards show actual performance metrics. Multi-schema tabs handle properties, clients, and transactions separately. CSV import updates listings across all branches instantly.'
  },
  {
    domain: 'Education',
    icon: Globe,
    problem: 'Student records scattered across departments. Admission counselors across branches double-contact the same prospects. Scholarship documents get lost in email chains. No unified view of enrollment pipeline.',
    solution: 'Unified student schema with branch-level access. Advanced filtering finds any student in seconds. Document management keeps transcripts, IDs, and certificates organized per record. Dashboard shows enrollment funnel by branch.'
  },
  {
    domain: 'Logistics & Distribution',
    icon: TreeStructure,
    problem: 'Warehouse teams, delivery branches, and HQ all use different tracking methods. Shipment status updates take hours to propagate. Nobody can generate a report without calling three people.',
    solution: 'One schema for shipments, another for inventory. Real-time status tracking with custom statuses and colors. Webhook integrations push updates to external systems. CSV export gives instant reports without phone calls.'
  },
  {
    domain: 'Telecom',
    icon: WifiHigh,
    problem: 'Customer complaints bounce between regional offices. SLA tracking is manual. Field technicians get assigned the same ticket twice. Escalation depends on whoever notices first.',
    solution: 'Auto-allocation routes tickets based on region and issue type. Interaction logging tracks every customer touchpoint. Reminder system enforces SLA timelines. Branch performance dashboards highlight bottleneck regions.'
  },
  {
    domain: 'Government & NGOs',
    icon: ShieldCheck,
    problem: 'Beneficiary data sits in district offices with no central visibility. Field surveys come in as CSVs that nobody processes for weeks. Audit compliance requires paper trails that don\'t exist digitally.',
    solution: 'Upload field survey CSVs directly. Data flows to the right district automatically. Full audit logging satisfies compliance requirements. Temporary access links allow inter-department reviews without compromising data security.'
  },
  {
    domain: 'Retail & Franchises',
    icon: Gear,
    problem: 'Franchise locations operate as data islands. HQ can\'t compare performance. Product returns and complaints don\'t reach the right department. Monthly reports arrive late and in incompatible formats.',
    solution: 'Standardized schemas across all locations. Branch performance visible in real-time dashboards. Document management handles return forms and complaints with previews. Automated allocation assigns issues to regional managers.'
  },
  {
    domain: 'Freelancing & Consulting',
    icon: UserCircle,
    problem: 'You\'re one person managing 20 clients across 5 industries. Proposals, invoices, and contracts live in random folders. Follow-ups depend on your memory. Scaling means drowning.',
    solution: 'Create a schema per client type. Track interactions per engagement. Reminder system keeps follow-ups on schedule. Document management stores proposals and contracts per client record. When you hire, role-based access keeps client data segregated.'
  },
];

/* ───────── principles ───────── */
const PRINCIPLES = [
  { icon: Database, title: 'Self-Hosted & Sovereign', desc: 'Your data lives on your servers. Not on ours. Not in a shared cloud. Your infrastructure, your rules, your peace of mind.' },
  { icon: Lock, title: 'Intranet-First Security', desc: 'Deploy behind your firewall. No external API calls required for core operations. Your customer data never leaves your network.' },
  { icon: ShieldCheck, title: 'Transparent & Auditable', desc: 'Every action is logged. Every access is tracked. Full audit trails that satisfy the strictest compliance requirements.' },
  { icon: Globe, title: 'No Vendor Lock-in', desc: 'Open architecture. Standard MongoDB. Standard file storage. Export everything anytime. If you leave, your data comes with you — all of it.' },
];

/* ───────── process steps ───────── */
const PROCESS_STEPS = [
  { num: '01', title: 'Upload Your Data', desc: 'Drop a CSV. The system reads your columns, detects types, and creates a schema automatically.' },
  { num: '02', title: 'Map to Branches', desc: 'Assign a branch field. Records auto-distribute to the right branch based on your mapping.' },
  { num: '03', title: 'Set the Rules', desc: 'Define who gets what. Auto-allocation, access controls, and field permissions — all configurable.' },
  { num: '04', title: 'Let it Work', desc: 'Your team logs in, sees their records, tracks interactions, uploads documents. You watch the dashboard.' },
];

/* ═══════════════════════════ COMPONENT ═══════════════════════════ */
const LandingPage = () => {
  const navigate = useNavigate();
  const heroText = useTypewriter(TYPEWRITER_LINES);
  const [activeTab, setActiveTab] = useState(0);
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [appInfo, setAppInfo] = useState({ app_name: 'Allocation Manager', tagline: 'Decentralized Operations Platform' });

  // Load theme
  useEffect(() => {
    const loadTheme = async () => {
      try {
        const [themeRes, infoRes] = await Promise.all([
          fetch(`${API_URL}/api/public/theme`),
          fetch(`${API_URL}/api/public/app-info`)
        ]);
        const theme = await themeRes.json();
        const info = await infoRes.json();
        setAppInfo(info);
        if (theme.colors && Object.keys(theme.colors).length > 0) {
          const c = theme.colors;
          const root = document.documentElement;
          if (c.primary) root.style.setProperty('--primary', hexToHSL(c.primary));
          if (c.background) root.style.setProperty('--background', hexToHSL(c.background));
          if (c.accent) root.style.setProperty('--accent', hexToHSL(c.accent));
          if (c.text_primary) root.style.setProperty('--foreground', hexToHSL(c.text_primary));
          if (c.card) root.style.setProperty('--card', hexToHSL(c.card));
          if (c.primary_foreground) root.style.setProperty('--primary-foreground', hexToHSL(c.primary_foreground));
        }
      } catch (e) { /* fallback to defaults */ }
    };
    loadTheme();
  }, []);

  const toggleFlip = (i) => {
    setFlippedCards(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  return (
    <div className="landing-page font-bricolage min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* ─── BG ANIMATION ─── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="landing-orb landing-orb-1" />
        <div className="landing-orb landing-orb-2" />
        <div className="landing-orb landing-orb-3" />
        <div className="landing-grid" />
      </div>

      {/* ─── NAV ─── */}
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5 backdrop-blur-sm border-b border-border/40">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <Lightning size={18} className="text-primary-foreground" weight="fill" />
          </div>
          <span className="font-bold text-lg tracking-tight">{appInfo.app_name}</span>
        </div>
        <Button onClick={() => navigate('/login')} className="rounded-full px-6" data-testid="nav-login-btn">
          Sign In <ArrowRight size={16} className="ml-1" />
        </Button>
      </nav>

      {/* ═══════ HERO ═══════ */}
      <section className="relative z-10 px-6 md:px-12 pt-24 pb-20 max-w-5xl mx-auto text-center" data-testid="hero-section">
        <div className="inline-block px-4 py-1.5 rounded-full border border-primary/30 bg-primary/5 text-primary text-sm font-medium mb-6 animate-fade-in">
          Operations management, reimagined
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 tracking-tight">
          Your branches.{' '}
          <span className="text-primary">One platform.</span>
        </h1>
        <div className="h-16 sm:h-12 flex items-center justify-center mb-8">
          <p className="text-xl sm:text-2xl text-muted-foreground" data-testid="typewriter-text">
            {heroText}<span className="animate-blink text-primary font-light">|</span>
          </p>
        </div>
        <div className="flex gap-4 justify-center flex-wrap">
          <Button size="lg" onClick={() => navigate('/login')} className="rounded-full px-8 text-base" data-testid="hero-cta">
            Get Started <ArrowRight size={18} className="ml-2" />
          </Button>
          <Button size="lg" variant="outline" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="rounded-full px-8 text-base">
            See Features
          </Button>
        </div>
      </section>

      {/* ═══════ HOW IT WORKS ═══════ */}
      <section className="relative z-10 px-6 md:px-12 py-20 max-w-6xl mx-auto" data-testid="process-section">
        <h2 className="text-lg font-semibold text-primary mb-2 text-center">How it works</h2>
        <p className="text-3xl sm:text-4xl font-bold text-center mb-14 tracking-tight">Four steps. That&apos;s it.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PROCESS_STEPS.map((step, i) => (
            <div key={i} className="group relative p-6 rounded-2xl border border-border/50 bg-card/60 backdrop-blur-sm hover:border-primary/40 transition-all duration-300 hover:-translate-y-1">
              <span className="text-5xl font-black text-primary/10 group-hover:text-primary/20 transition-colors absolute top-4 right-4">{step.num}</span>
              <h3 className="text-lg font-bold mb-2 mt-2">{step.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════ FEATURES ═══════ */}
      <section id="features" className="relative z-10 px-6 md:px-12 py-20 max-w-6xl mx-auto" data-testid="features-section">
        <h2 className="text-lg font-semibold text-primary mb-2 text-center">Features</h2>
        <p className="text-3xl sm:text-4xl font-bold text-center mb-4 tracking-tight">Everything you need. Nothing you don&apos;t.</p>
        <p className="text-muted-foreground text-center mb-14 max-w-2xl mx-auto">Click any card to reveal a hidden message.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            const isFlipped = flippedCards.has(i);
            return (
              <div
                key={i}
                className="landing-flip-card cursor-pointer"
                style={{ perspective: '1000px', height: '220px' }}
                onClick={() => toggleFlip(i)}
                data-testid={`feature-card-${i}`}
              >
                <div
                  className={`landing-flip-inner relative w-full h-full transition-transform duration-500`}
                  style={{ transformStyle: 'preserve-3d', transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0)' }}
                >
                  {/* Front */}
                  <div className="absolute inset-0 backface-hidden rounded-2xl border border-border/50 bg-card/70 backdrop-blur-sm p-5 flex flex-col" style={{ backfaceVisibility: 'hidden' }}>
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                      <Icon size={22} className="text-primary" weight="duotone" />
                    </div>
                    <h3 className="font-bold text-sm mb-1.5">{f.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed flex-1">{f.desc}</p>
                    <p className="text-[10px] text-muted-foreground/50 mt-2 italic">click to flip</p>
                  </div>
                  {/* Back */}
                  <div className="absolute inset-0 backface-hidden rounded-2xl border border-primary/30 bg-primary/5 backdrop-blur-sm p-5 flex flex-col items-center justify-center text-center" style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
                    <Icon size={28} className="text-primary mb-3" weight="fill" />
                    <p className="text-sm font-medium leading-relaxed italic">&ldquo;{f.joke}&rdquo;</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ═══════ PAIN POINTS (vertical tabs) ═══════ */}
      <section className="relative z-10 px-6 md:px-12 py-20 max-w-6xl mx-auto" data-testid="painpoints-section">
        <h2 className="text-lg font-semibold text-primary mb-2 text-center">Industry Solutions</h2>
        <p className="text-3xl sm:text-4xl font-bold text-center mb-14 tracking-tight">Your industry. Your pain. Our fix.</p>
        <div className="flex flex-col lg:flex-row gap-6 min-h-[420px]">
          {/* Tab list */}
          <div className="lg:w-64 shrink-0 flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" data-testid="painpoint-tabs">
            {PAIN_POINTS.map((pp, i) => {
              const Icon = pp.icon;
              return (
                <button
                  key={i}
                  onClick={() => setActiveTab(i)}
                  className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-left text-sm font-medium whitespace-nowrap transition-all duration-200 ${activeTab === i ? 'bg-primary text-primary-foreground shadow-md' : 'bg-card/50 hover:bg-secondary/60 text-muted-foreground'}`}
                  data-testid={`painpoint-tab-${i}`}
                >
                  <Icon size={18} weight={activeTab === i ? 'fill' : 'regular'} />
                  {pp.domain}
                </button>
              );
            })}
          </div>
          {/* Content panel */}
          <div className="flex-1 rounded-2xl border border-border/50 bg-card/60 backdrop-blur-sm p-8" data-testid="painpoint-content">
            {(() => {
              const pp = PAIN_POINTS[activeTab];
              const Icon = pp.icon;
              return (
                <div key={activeTab} className="animate-fade-in">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon size={24} className="text-primary" weight="duotone" />
                    </div>
                    <h3 className="text-xl font-bold">{pp.domain}</h3>
                  </div>

                  <div className="mb-8">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-2 h-2 rounded-full bg-destructive" />
                      <h4 className="font-semibold text-sm uppercase tracking-wide text-destructive">The Pain</h4>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{pp.problem}</p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-2 h-2 rounded-full bg-primary" />
                      <h4 className="font-semibold text-sm uppercase tracking-wide text-primary">The Fix</h4>
                    </div>
                    <p className="text-foreground/90 leading-relaxed">{pp.solution}</p>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      {/* ═══════ PRINCIPLES ═══════ */}
      <section className="relative z-10 px-6 md:px-12 py-20 max-w-5xl mx-auto" data-testid="principles-section">
        <h2 className="text-lg font-semibold text-primary mb-2 text-center">Core Principles</h2>
        <p className="text-3xl sm:text-4xl font-bold text-center mb-14 tracking-tight">We believe your data is yours. Period.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {PRINCIPLES.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="flex gap-4 p-6 rounded-2xl border border-border/50 bg-card/60 backdrop-blur-sm">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon size={24} className="text-primary" weight="duotone" />
                </div>
                <div>
                  <h3 className="font-bold mb-1">{p.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ═══════ CTA ═══════ */}
      <section className="relative z-10 px-6 md:px-12 py-24 max-w-4xl mx-auto text-center" data-testid="cta-section">
        <div className="rounded-3xl border border-primary/20 bg-primary/5 backdrop-blur-sm p-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 tracking-tight">Ready to stop firefighting?</h2>
          <p className="text-muted-foreground mb-2 max-w-xl mx-auto">
            Let&apos;s set up your branches, import your data, and have you running in under a day.
          </p>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Write to us at{' '}
            <a href="mailto:hello@krri.sh" className="text-primary font-semibold hover:underline">hello@krri.sh</a>
            {' '}&mdash; we get back in under a week.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a href="mailto:hello@krri.sh">
              <Button size="lg" className="rounded-full px-8 text-base">
                <Envelope size={18} className="mr-2" /> Contact Us
              </Button>
            </a>
            <Button size="lg" variant="outline" onClick={() => navigate('/login')} className="rounded-full px-8 text-base">
              Sign In <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="relative z-10 border-t border-border/40 px-6 md:px-12 py-8 text-center">
        <p className="text-sm text-muted-foreground">
          {appInfo.app_name} &mdash; {appInfo.tagline}
        </p>
        <p className="text-xs text-muted-foreground/60 mt-1">Self-hosted. Data-sovereign. Built for teams that care.</p>
      </footer>
    </div>
  );
};

export default LandingPage;
